import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { overlayLayers } from '../ui/overlayLayers';

const AiFabSuppressionContext = createContext({
  suppressed: false,
  suppressedUnlessDocked: false,
  incrementSuppressor: () => {},
  decrementSuppressor: () => {},
});

/**
 * Tracks how many overlays currently want the AI floating button hidden.
 *
 * Two counters (KAN-1011):
 *  - `hard` — hide the button everywhere (full-screen flows, previews, the bulk
 *    bar, gates): they cover or replace the bottom bar.
 *  - `soft` — hide it ONLY when it floats on its own (desktop corner, screens
 *    without the bar). A button docked in the bottom bar stays mounted and dims
 *    under the overlay's scrim together with the bar, so it returns on the same
 *    frame as the bar instead of re-mounting after the overlay's exit.
 *
 * Counters, so nested/sibling overlays don't race each other — each consumer
 * increments on mount and decrements on unmount.
 */
export function AiFabSuppressionProvider({ children }) {
  const [counts, setCounts] = useState({ hard: 0, soft: 0 });

  const incrementSuppressor = useCallback((kind = 'hard') => {
    setCounts((c) => ({ ...c, [kind]: c[kind] + 1 }));
  }, []);

  const decrementSuppressor = useCallback((kind = 'hard') => {
    setCounts((c) => ({ ...c, [kind]: Math.max(0, c[kind] - 1) }));
  }, []);

  const suppressed = counts.hard > 0;
  const suppressedUnlessDocked = counts.soft > 0;
  const value = useMemo(
    () => ({ suppressed, suppressedUnlessDocked, incrementSuppressor, decrementSuppressor }),
    [suppressed, suppressedUnlessDocked, incrementSuppressor, decrementSuppressor],
  );

  return (
    <AiFabSuppressionContext.Provider value={value}>
      {children}
    </AiFabSuppressionContext.Provider>
  );
}

/** Read-only: true when any consumer wants the AI FAB hidden, docked or not. */
export function useIsAiFabSuppressed() {
  return useContext(AiFabSuppressionContext).suppressed;
}

/** Read-only: true when an overlay wants the FAB hidden unless it sits in the bottom bar. */
export function useIsAiFabSuppressedUnlessDocked() {
  return useContext(AiFabSuppressionContext).suppressedUnlessDocked;
}

/** True when an overlay's scrim z-index paints above the bottom bar and the desktop rail (and the docked AI button in either). */
export function scrimCoversBottomBar(zIndex) {
  return typeof zIndex === 'number' && zIndex > Math.max(overlayLayers.bottomTabChrome, overlayLayers.navRail);
}

/**
 * Call inside an overlay to hide the AI FAB while it's mounted.
 * `keepDocked`: the overlay's scrim sits above the bottom bar, so a docked button
 * can stay (dimmed with the bar) — only a free-floating button hides.
 */
export function useSuppressAiFab(active = true, { keepDocked = false } = {}) {
  const { incrementSuppressor, decrementSuppressor } = useContext(AiFabSuppressionContext);
  const kind = keepDocked ? 'soft' : 'hard';
  useEffect(() => {
    if (!active) return undefined;
    incrementSuppressor(kind);
    return () => decrementSuppressor(kind);
  }, [active, kind, incrementSuppressor, decrementSuppressor]);
}
